import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  List, 
  Tag, 
  Button, 
  Space, 
  Modal,
  Divider,
  message,
  Spin,
  Popconfirm
} from 'antd';
import { 
  HistoryOutlined, 
  EyeOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { lawService } from '../../services/lawService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { LawVersion, Law, LawStatus, Attachment } from '../../types';
import { formatLocalizedDateTime, formatLocalizedDate } from '../../utils/dateUtils';

import '../../styles/contentStyles.css';

// HTML 태그와 스타일을 제거하여 순수 텍스트만 추출하는 함수
const extractTextFromHtml = (content: string): string => {
  if (!content) return content;

  // HTML 태그 제거 (단, <br>, <p>, <div> 태그는 공백으로 변환)
  let text = content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]*>/g, '') // 모든 HTML 태그 제거
    .replace(/&nbsp;/g, ' ') // &nbsp;를 공백으로 변환
    .replace(/&lt;/g, '<') // &lt;를 <로 변환
    .replace(/&gt;/g, '>') // &gt;를 >로 변환
    .replace(/&amp;/g, '&') // &amp;를 &로 변환
    .replace(/&quot;/g, '"') // &quot;를 "로 변환
    .replace(/&#39;/g, "'") // &#39;를 '로 변환
    .replace(/\n\s*\n/g, '\n') // 연속된 줄바꿈을 하나로
    .trim();

  return text;
};

const { Title, Text } = Typography;

// 버전 보기용 CSS 스타일 - 편집 버튼 숨김
const versionStyles = `
  .preview-content .add-row-btn,
  .preview-content .delete-row-btn,
  .preview-content .table-controls {
    display: none !important;
  }
  
  .preview-content table td[contenteditable="true"] {
    cursor: default;
  }
  
  .preview-content table td[contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  /* 버전 비교 모달 내부 스타일 */
  .version-modal .add-row-btn,
  .version-modal .delete-row-btn,
  .version-modal .table-controls {
    display: none !important;
  }
  
  .version-modal table td[contenteditable="true"] {
    cursor: default;
  }
  
  .version-modal table td[contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  /* 제목 스타일 적용 */
  .preview-content h1,
  .preview-content h2,
  .preview-content h3,
  .preview-content h4,
  .preview-content h5,
  .preview-content h6 {
    font-weight: bold;
    margin: 16px 0 8px 0;
    color: #2c3e50;
  }
  
  .preview-content h1 {
    font-size: 24px;
    border-bottom: 2px solid #1890ff;
    padding-bottom: 8px;
  }
  
  .preview-content h2 {
    font-size: 20px;
    border-bottom: 1px solid #d9d9d9;
    padding-bottom: 6px;
  }
  
  .preview-content h3 {
    font-size: 18px;
  }
  
  .preview-content h4 {
    font-size: 16px;
  }
  
  .preview-content h5 {
    font-size: 14px;
  }
  
  .preview-content h6 {
    font-size: 12px;
  }
  
  /* 테이블 스타일 */
  .preview-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
  }
  
  .preview-content table th,
  .preview-content table td {
    border: 1px solid #d9d9d9;
    padding: 8px 12px;
    text-align: left;
  }
  
  .preview-content table th {
    background-color: #f5f5f5;
    font-weight: bold;
  }
  
  /* 리스트 스타일 */
  .preview-content ul,
  .preview-content ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  .preview-content li {
    margin: 4px 0;
  }
  
  /* 단락 스타일 */
  .preview-content p {
    margin: 8px 0;
    line-height: 1.6;
  }
  
  /* 버전 보기용 스타일 클래스 적용 */
  .preview-content .title-style,
  .preview-content .pub-title-style {
    font-size: 24px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 20px 0 16px 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
  }
  
  .preview-content .jang-style,
  .preview-content .pub-jang-style {
    font-size: 18px !important;
    font-weight: bold !important;
    text-align: left !important;
    padding: 40px 0 0 80px !important;
    margin: 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
    border: none !important;
    background: none !important;
    outline: none !important;
  }
  
  .preview-content .jo-style,
  .preview-content .pub-jo-style {
    padding: 16px 0 0px 40px !important;
    text-indent: -40px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .hang-style,
  .preview-content .pub-hang-style {
    padding: 0px 0 0px 60px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .ho-style,
  .preview-content .pub-ho-style {
    padding: 0px 0 0px 80px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  /* 버전 비교 모달 내부 스타일 클래스 적용 */
  .version-modal .title-style,
  .version-modal .pub-title-style {
    font-size: 24px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 20px 0 16px 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
  }
  
  .version-modal .jang-style,
  .version-modal .pub-jang-style {
    font-size: 18px !important;
    font-weight: bold !important;
    text-align: left !important;
    padding: 40px 0 0 80px !important;
    margin: 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
    border: none !important;
    background: none !important;
    outline: none !important;
  }
  
  .version-modal .jo-style,
  .version-modal .pub-jo-style {
    padding: 16px 0 0px 40px !important;
    text-indent: -40px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .version-modal .hang-style,
  .version-modal .pub-hang-style {
    padding: 0px 0 0px 60px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .version-modal .ho-style,
  .version-modal .pub-ho-style {
    padding: 0px 0 0px 80px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
`;

const LawVersions: React.FC = () => {
  const { id: lawId } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const [versions, setVersions] = useState<LawVersion[]>([]);
  const [currentLaw, setCurrentLaw] = useState<Law | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<LawVersion | null>(null);
  const [compareModalVisible, setCompareModalVisible] = useState(false);
  const [compareData, setCompareData] = useState<any>(null);
  const [compareLoading, setCompareLoading] = useState(false);

  const fetchVersions = useCallback(async () => {
    if (!lawId) return;
    
    setLoading(true);
    try {
      const data = await lawService.getLawVersions(lawId);
      // 일반 사용자는 승인된 버전만 볼 수 있음
      const filteredVersions = isAdmin ? data : data.filter(version => version.is_approved);
      setVersions(filteredVersions);
    } catch (error: any) {
      message.error(t('laws.versions.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [lawId, isAdmin, t]);

  const fetchCurrentLaw = useCallback(async () => {
    if (!lawId) return;
    
    try {
      const data = await lawService.getLaw(lawId);
      setCurrentLaw(data);
    } catch (error: any) {
      message.error(t('laws.versions.currentLawLoadFailed'));
    }
  }, [lawId, t]);

  useEffect(() => {
    fetchVersions();
    fetchCurrentLaw();
  }, [fetchVersions, fetchCurrentLaw]);

  const handleViewVersion = (version: LawVersion) => {
    setSelectedVersion(version);
  };

  const handleCompareWithPrevious = async (version: LawVersion, index: number) => {
    if (!currentLaw || !lawId) {
      message.error(t('laws.versions.currentLawLoadFailed'));
      return;
    }

    setCompareLoading(true);
    try {
      // n번째 버전의 경우: n+1번째를 기준으로 n번째와 비교
      const data = await lawService.compareVersions(lawId, {
        versionId1: versions[index + 1].version_id, // n+1번째 버전 (기준)
        versionId2: version.version_id, // n번째 버전
      });
      setCompareData(data);
      setCompareModalVisible(true);
    } catch (error: any) {
      message.error(t('laws.versions.compareFailed'));
    } finally {
      setCompareLoading(false);
    }
  };

  const handleDeleteVersion = async (versionId: string) => {
    if (!lawId) return;
    
    try {
      await lawService.deleteLawVersion(lawId, versionId);
      message.success(t('laws.versions.deleteSuccess'));
      fetchVersions();
    } catch (error: any) {
      message.error(t('laws.versions.deleteFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      message.error(t('laws.versions.downloadFailed'));
    }
  };

  const getStatusColor = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return 'green';
      case LawStatus.AMENDED:
        return 'blue';
      case LawStatus.FULLY_AMENDED:
        return 'purple';
      case LawStatus.ABOLISHED:
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return t('laws.enacted');
      case LawStatus.AMENDED:
        return t('laws.amended');
      case LawStatus.FULLY_AMENDED:
        return t('laws.fullyAmended');
      case LawStatus.ABOLISHED:
        return t('laws.abolished');
      default:
        return status;
    }
  };

  const renderVersionContent = (content: string, title?: string, category?: string, resolution_date?: string, serial_number?: number, status?: LawStatus) => {
            const formattedDate = resolution_date ? formatLocalizedDate(resolution_date, i18n.language) : '';
    
    return (
      <div 
        style={{ 
          backgroundColor: '#fafafa',
          padding: '16px',
          borderRadius: '4px',
          margin: '8px 0'
        }}
      >
        {/* 메타 정보를 균형잡힌 스타일로 표시 */}
        <div style={{ 
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{
            fontSize: '16px',
            marginBottom: '12px',
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{
              color: '#666',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '60px',
              flexShrink: 0
            }}>{t('laws.versions.labels.title')}</span>
            <span style={{ fontWeight: '500' }}>{title || '-'}</span>
          </div>
          <div style={{ 
            fontSize: '16px', 
            marginBottom: '12px',
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ 
              color: '#666', 
              fontSize: '14px', 
              fontWeight: '500',
              minWidth: '60px',
              flexShrink: 0
            }}>{t('laws.versions.labels.documentType')}</span>
            <span style={{ fontWeight: '500' }}>
              {status ? (
                <Tag color={getStatusColor(status)} style={{ margin: 0 }}>
                  {getStatusText(status)}
                </Tag>
              ) : '-'}
            </span>
          </div>
          <div style={{ 
            fontSize: '16px', 
            marginBottom: '12px',
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ 
              color: '#666', 
              fontSize: '14px', 
              fontWeight: '500',
              minWidth: '60px',
              flexShrink: 0
            }}>{t('laws.versions.labels.number')}</span>
            <span style={{ fontWeight: '500' }}>
              {category && serial_number && serial_number !== 0 ? `${category} ${i18n.language === 'ko' ? `제${serial_number}호` : `No.${serial_number}`}` : category || '-'}
            </span>
          </div>
          {/* 관리처: 결의일자 전에 위치 */}
          {(
            <div style={{ 
              fontSize: '16px',
              marginBottom: '12px',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{
                color: '#666',
                fontSize: '14px',
                fontWeight: '500',
                minWidth: '60px',
                flexShrink: 0
              }}>{t('laws.detail.labels.owner')}</span>
              <span style={{ fontWeight: '500' }}>{(selectedVersion ? selectedVersion.owner : currentLaw?.owner) || '-'}</span>
            </div>
          )}
          <div style={{ 
            fontSize: '16px',
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ 
              color: '#666', 
              fontSize: '14px', 
              fontWeight: '500',
              minWidth: '60px',
              flexShrink: 0
            }}>{t('laws.versions.labels.resolutionDate')}</span>
            <span style={{ fontWeight: '500' }}>
              {formattedDate || '-'}
            </span>
          </div>
        </div>
        
        {/* 내용 */}
        <div 
          className="preview-content"
          style={{ 
            fontSize: '14px', 
            lineHeight: '1.6', 
            maxHeight: '200px',
            overflow: 'auto'
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    );
  };

  // 버전 표시 형식 생성 함수
  const getVersionDisplayName = (version: LawVersion, index: number) => {
    // 분류와 일련번호를 사용하여 표시
    const category = version.category || currentLaw?.category || '';
    const serialNumber = version.serial_number || currentLaw?.serial_number;
    
    if (index === 0) {
      // 현재 규범인 경우
      if (category && serialNumber && serialNumber !== 0) {
        return `${t('laws.versions.display.currentLaw')}(${category} ${i18n.language === 'ko' ? `제${serialNumber}호` : `No.${serialNumber}`})`;
      } else if (category) {
        return `${t('laws.versions.display.currentLaw')}(${category})`;
      } else {
        return t('laws.versions.display.currentLaw');
      }
    }
    
    // 이전 이력들
    if (category && serialNumber && serialNumber !== 0) {
      return `${category} ${i18n.language === 'ko' ? `제${serialNumber}호` : `No.${serialNumber}`}`;
    } else if (category) {
      return `${category}`;
    } else {
      return `${t('laws.versions.display.versionNumber', { number: versions.length - index })}`;
    }
  };

  const renderDiffContent = (diffs: any[]) => {
    if (diffs.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
          <Text>{t('laws.versions.noCompareContent')}</Text>
        </div>
      );
    }

    return (
      <div 
        className="preview-content"
        style={{ 
          fontSize: '14px',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap' // 줄바꿈 유지
        }}
      >
        {diffs.map((diff, index) => {
          // HTML 태그와 스타일을 제거하여 순수 텍스트만 표시
          const cleanText = extractTextFromHtml(diff.text);
          
          return (
            <span
              key={index}
              style={{
                backgroundColor: diff.type === 'insert' ? '#d4edda' : 
                               diff.type === 'delete' ? '#f8d7da' : 'transparent',
                color: diff.type === 'insert' ? '#155724' : 
                       diff.type === 'delete' ? '#721c24' : 'inherit',
                textDecoration: diff.type === 'delete' ? 'line-through' : 'none',
                padding: '1px 2px',
                borderRadius: '2px',
                border: diff.type === 'insert' ? '1px solid #c3e6cb' : 
                        diff.type === 'delete' ? '1px solid #f5c6cb' : 'none'
              }}
            >
              {cleanText}
            </span>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin />
        <div style={{ marginTop: '8px' }}>{t('laws.versions.loading')}</div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <HistoryOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
          <Title level={4} style={{ marginTop: '16px', color: '#8c8c8c' }}>
            {t('laws.versions.noVersions')}
          </Title>
          <Text type="secondary">
            {t('laws.versions.noVersionsDesc')}
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <HistoryOutlined /> {t('laws.versions.title')} ({versions.length}개)
          </Title>
        </div>

        <List
          dataSource={versions}
          renderItem={(version, index) => (
            <List.Item
              key={version.version_id}
              actions={[
                ...(isAdmin && index > 0 ? [
                  <Popconfirm
                    key="delete"
                    title={t('laws.versions.confirmDelete')}
                    onConfirm={() => handleDeleteVersion(version.version_id)}
                    okText={t('laws.versions.actions.delete')}
                    cancelText={t('common.cancel')}
                  >
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                    >
                      {t('laws.versions.actions.delete')}
                    </Button>
                  </Popconfirm>
                ] : []),
                ...(index < versions.length - 1 ? [
                  <Button
                    key="compare"
                    type="text"
                    icon={<SwapOutlined />}
                    onClick={() => handleCompareWithPrevious(version, index)}
                    loading={compareLoading}
                  >
                    {t('laws.versions.actions.compare')}
                  </Button>
                ] : []),
                <Button
                  key="view"
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewVersion(version)}
                >
                  {t('laws.versions.actions.view')}
                </Button>
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>
                      {getVersionDisplayName(version, index)}
                    </Text>
                    {isAdmin && (
                      <>
                        <Tag color={version.is_approved ? 'green' : 'orange'}>
                          {version.is_approved ? t('laws.versions.status.approved') : t('laws.versions.status.unapproved')}
                        </Tag>
                        {version.is_approved && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                      </>
                    )}
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    {isAdmin && (
                      <Text type="secondary">
                        <ClockCircleOutlined /> {formatLocalizedDateTime(version.created_at, i18n.language)}
                      </Text>
                    )}
                    {version.resolution_date && (
                      <Text type="secondary">
                        {t('laws.versions.labels.resolutionDate')} {formatLocalizedDate(version.resolution_date, i18n.language)}
                      </Text>
                    )}
                    {version.attachments && version.attachments.length > 0 && (
                      <div>
                        <Text type="secondary" style={{ marginRight: 8 }}>
                          {t('laws.versions.labels.attachmentsCount', { count: version.attachments.length })}
                        </Text>
                        <Space size="small">
                          {version.attachments.slice(0, 2).map((attachment) => (
                            <Button
                              key={attachment.attachment_id}
                              type="text"
                              size="small"
                              icon={<span style={{ fontSize: '12px' }}>📎</span>}
                              onClick={() => handleDownloadAttachment(attachment)}
                              style={{ 
                                padding: '1px 3px', 
                                height: '20px',
                                fontSize: '11px'
                              }}
                              title={decodeURIComponent(attachment.file_name)}
                            >
                              {decodeURIComponent(attachment.file_name).substring(0, 10)}
                              {decodeURIComponent(attachment.file_name).length > 10 ? '...' : ''}
                            </Button>
                          ))}
                          {version.attachments.length > 2 && (
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              {t('laws.versions.labels.moreAttachments', { count: version.attachments.length - 2 })}
                            </Text>
                          )}
                        </Space>
                      </div>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      {/* 이력 내용 보기 모달 */}
      <Modal
        title={t('laws.versions.modal.viewTitle')}
        open={!!selectedVersion}
        onCancel={() => setSelectedVersion(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedVersion(null)}>
            {t('laws.versions.modal.close')}
          </Button>
        ]}
        width={800}
        className="version-modal"
      >
        <style>{versionStyles}</style>
        {selectedVersion && (
          <div>
            {isAdmin && (
              <div style={{ marginBottom: 24 }}>
                <Space size="middle">
                  <Tag color={selectedVersion.is_approved ? 'green' : 'orange'}>
                    {selectedVersion.is_approved ? t('laws.versions.status.approved') : t('laws.versions.status.unapproved')}
                  </Tag>
                  <Text type="secondary">
                    {t('laws.versions.labels.createdAt')} {formatLocalizedDateTime(selectedVersion.created_at, i18n.language)}
                  </Text>
                </Space>
              </div>
            )}
            <Divider />
            
            {/* 첨부파일 */}
            {selectedVersion.attachments && selectedVersion.attachments.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>{t('laws.detail.attachments')}</Title>
                <Card>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {selectedVersion.attachments.map((attachment) => (
                      <div
                        key={attachment.attachment_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          border: '1px solid #f0f0f0',
                          borderRadius: '6px',
                          backgroundColor: '#fafafa'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '20px' }}>📎</span>
                          <div>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                              {decodeURIComponent(attachment.file_name)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {attachmentService.formatFileSize(attachment.file_size)} • {formatLocalizedDateTime(attachment.uploaded_at, i18n.language)}
                            </div>
                          </div>
                        </div>
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleDownloadAttachment(attachment)}
                        >
                          {t('laws.versions.actions.download')}
                        </Button>
                      </div>
                    ))}
                  </Space>
                </Card>
              </div>
            )}
            
            {renderVersionContent(selectedVersion.content, selectedVersion.title, selectedVersion.category, selectedVersion.resolution_date, selectedVersion.serial_number, selectedVersion.status)}
          </div>
        )}
      </Modal>

      {/* 이력 비교 모달 */}
      <Modal
        title={t('laws.versions.modal.compareTitle')}
        open={compareModalVisible}
        onCancel={() => setCompareModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setCompareModalVisible(false)}>
            {t('laws.versions.modal.close')}
          </Button>
        ]}
        width={1000}
        className="version-modal"
      >
        <style>{versionStyles}</style>
        {compareData && (
          <div>
            {isAdmin && (
              <div style={{ marginBottom: 24 }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{t('laws.versions.compare.previousVersion')}</Text>
                    <Text style={{ marginLeft: 12 }}>{formatLocalizedDateTime(compareData.version1.created_at, i18n.language)}</Text>
                  </div>
                  <div>
                    <Text strong>{t('laws.versions.compare.currentVersion')}</Text>
                    <Text style={{ marginLeft: 12 }}>{formatLocalizedDateTime(compareData.version2.created_at, i18n.language)}</Text>
                  </div>
                </Space>
              </div>
            )}
            
            {/* 제목 비교 */}
            {compareData.version1.title !== compareData.version2.title && (
              <>
                <Divider orientation="left">{t('laws.versions.compare.titleChange')}</Divider>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>{t('laws.versions.compare.previous')}</Text>
                    <Text style={{ marginLeft: 12, textDecoration: 'line-through', color: '#721c24' }}>
                      {compareData.version1.title}
                    </Text>
                  </div>
                  <div>
                    <Text strong>{t('laws.versions.compare.current')}</Text>
                    <Text style={{ marginLeft: 12, color: '#155724' }}>
                      {compareData.version2.title}
                    </Text>
                  </div>
                </div>
              </>
            )}
            
            {/* 분류 비교 */}
            {compareData.version1.category !== compareData.version2.category && (
              <>
                <Divider orientation="left">{t('laws.versions.compare.categoryChange')}</Divider>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>{t('laws.versions.compare.previous')}</Text>
                    <Text style={{ marginLeft: 12, textDecoration: 'line-through', color: '#721c24' }}>
                      {compareData.version1.category}
                    </Text>
                  </div>
                  <div>
                    <Text strong>{t('laws.versions.compare.current')}</Text>
                    <Text style={{ marginLeft: 12, color: '#155724' }}>
                      {compareData.version2.category}
                    </Text>
                  </div>
                </div>
              </>
            )}
            
            {/* 결의일자 비교 */}
            {compareData.version1.resolution_date !== compareData.version2.resolution_date && (
              <>
                <Divider orientation="left">{t('laws.versions.compare.resolutionDateChange')}</Divider>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>{t('laws.versions.compare.previous')}</Text>
                    <Text style={{ marginLeft: 12, textDecoration: 'line-through', color: '#721c24' }}>
                      {compareData.version1.resolution_date ? formatLocalizedDate(compareData.version1.resolution_date, i18n.language) : '-'}
                    </Text>
                  </div>
                  <div>
                    <Text strong>{t('laws.versions.compare.current')}</Text>
                    <Text style={{ marginLeft: 12, color: '#155724' }}>
                      {compareData.version2.resolution_date ? formatLocalizedDate(compareData.version2.resolution_date, i18n.language) : '-'}
                    </Text>
                  </div>
                </div>
              </>
            )}
            
            {/* 일련번호 비교 */}
            {compareData.version1.serial_number !== compareData.version2.serial_number && (
              <>
                <Divider orientation="left">{t('laws.versions.compare.serialNumberChange')}</Divider>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>{t('laws.versions.compare.previous')}</Text>
                    <Text style={{ marginLeft: 12, textDecoration: 'line-through', color: '#721c24' }}>
                      {compareData.version1.serial_number || '-'}
                    </Text>
                  </div>
                  <div>
                    <Text strong>{t('laws.versions.compare.current')}</Text>
                    <Text style={{ marginLeft: 12, color: '#155724' }}>
                      {compareData.version2.serial_number || '-'}
                    </Text>
                  </div>
                </div>
              </>
            )}
            
            {/* 문서구분 비교 */}
            {compareData.version1.status !== compareData.version2.status && (
              <>
                <Divider orientation="left">{t('laws.versions.compare.documentTypeChange')}</Divider>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>{t('laws.versions.compare.previous')}</Text>
                    <Text style={{ marginLeft: 12, textDecoration: 'line-through', color: '#721c24' }}>
                      {compareData.version1.status ? getStatusText(compareData.version1.status) : '-'}
                    </Text>
                  </div>
                  <div>
                    <Text strong>{t('laws.versions.compare.current')}</Text>
                    <Text style={{ marginLeft: 12, color: '#155724' }}>
                      {compareData.version2.status ? getStatusText(compareData.version2.status) : '-'}
                    </Text>
                  </div>
                </div>
              </>
            )}
            
            {/* 첨부파일 변경사항 */}
            {compareData.attachmentDiffs && compareData.attachmentDiffs.hasChanges && (
              <>
                <Divider orientation="left">{t('laws.versions.compare.attachmentChange')}</Divider>
                <div style={{ marginBottom: 20 }}>
                  {/* 추가된 첨부파일 */}
                  {compareData.attachmentDiffs.added.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: '#155724' }}>{t('laws.versions.compare.addedAttachments')}</Text>
                      <Card style={{ marginTop: 8, backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {compareData.attachmentDiffs.added.map((attachment: any) => (
                            <div
                              key={attachment.attachment_id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px',
                                border: '1px solid #b7eb8f',
                                borderRadius: '4px',
                                backgroundColor: '#f6ffed'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '16px' }}>📎</span>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                    {decodeURIComponent(attachment.file_name)}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {attachmentService.formatFileSize(attachment.file_size)}
                                  </div>
                                </div>
                              </div>
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => handleDownloadAttachment(attachment)}
                              >
                                {t('laws.versions.actions.download')}
                              </Button>
                            </div>
                          ))}
                        </Space>
                      </Card>
                    </div>
                  )}
                  
                  {/* 삭제된 첨부파일 */}
                  {compareData.attachmentDiffs.removed.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: '#721c24' }}>{t('laws.versions.compare.removedAttachments')}</Text>
                      <Card style={{ marginTop: 8, backgroundColor: '#fff2f0', borderColor: '#ffccc7' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {compareData.attachmentDiffs.removed.map((attachment: any) => (
                            <div
                              key={attachment.attachment_id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px',
                                border: '1px solid #ffccc7',
                                borderRadius: '4px',
                                backgroundColor: '#fff2f0',
                                textDecoration: 'line-through',
                                opacity: 0.7
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '16px' }}>📎</span>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                    {decodeURIComponent(attachment.file_name)}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {attachmentService.formatFileSize(attachment.file_size)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </Space>
                      </Card>
                    </div>
                  )}
                  
                  {/* 변경되지 않은 첨부파일 */}
                  {compareData.attachmentDiffs.unchanged.length > 0 && (
                    <div>
                      <Text strong style={{ color: '#666' }}>{t('laws.versions.compare.unchangedAttachments')}</Text>
                      <Card style={{ marginTop: 8, backgroundColor: '#fafafa', borderColor: '#d9d9d9' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {compareData.attachmentDiffs.unchanged.map((attachment: any) => (
                            <div
                              key={attachment.attachment_id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                backgroundColor: '#fafafa'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '16px' }}>📎</span>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                    {decodeURIComponent(attachment.file_name)}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {attachmentService.formatFileSize(attachment.file_size)}
                                  </div>
                                </div>
                              </div>
                              <Button
                                size="small"
                                onClick={() => handleDownloadAttachment(attachment)}
                              >
                                {t('laws.versions.actions.download')}
                              </Button>
                            </div>
                          ))}
                        </Space>
                      </Card>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* 내용 변경 */}
            <div style={{ marginTop: 20 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 16,
                padding: '8px 0',
                borderBottom: '1px solid #e8e8e8'
              }}>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>{t('laws.versions.compare.contentChange')}</Text>
              </div>
              <div style={{ 
                backgroundColor: '#fafafa', 
                padding: '16px', 
                borderRadius: '6px',
                maxHeight: '400px',
                overflow: 'auto',
                border: '1px solid #e8e8e8',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                {renderDiffContent(compareData.diffs)}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default LawVersions;